import type * as React from "react";
import { DayPicker } from "react-day-picker";

import { cn } from "../utils";
import { buttonVariants } from "./button";
import { Icon } from "./icon";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, components, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={true}
      captionLayout="dropdown-buttons"
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium flex gap-1 px-0.5 items-center relative",
        caption_dropdowns: `flex gap-2 text-sm [&_[class*=rdp-dropdown]]:relative [&_select]:z-10 [&_select]:absolute! [&_select]:inset-0! [&_select]:opacity-0 [&_.rdp-vhidden]:hidden`,
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "bg-transparent p-0 opacity-50 hover:opacity-100 size-7"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "p-0 font-normal size-8 text-foreground aria-selected:opacity-100"
        ),
        day_range_start: "day-range-start rounded-none rounded-l",
        day_range_end: "day-range-end rounded-none rounded-r",
        day_selected: "bg-foreground! text-background!",
        day_today: "bg-accent text-accent-foreground",
        day_outside: "text-muted-foreground opacity-50",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground rounded-none",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: () => <Icon icon="keyboard_arrow_left" />,
        IconRight: () => <Icon icon="keyboard_arrow_right" />,
        ...components,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
